import { useEffect, useState, useMemo, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import {
  Search, Calendar, MapPin, Phone, Mail, CheckCircle2, AlertTriangle,
  Clock, RefreshCw, XCircle, ArrowLeft,
} from "lucide-react";
import useSeo from "@/hooks/useSeo";
import { api, formatApiError } from "@/lib/api";

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function generateNext30Days() {
  const out = [];
  const today = new Date();
  for (let i = 1; i <= 30; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    out.push(d);
  }
  return out;
}

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

export default function CustomerPortal() {
  useSeo({
    title: "My Booking | Full Indoor Air Services",
    description: "View, reschedule, or cancel your air duct cleaning appointment.",
    canonical: "https://capitalindoorair.com/my-booking",
  });

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [conf, setConf] = useState((params.get("conf") || "").toUpperCase());
  const [email, setEmail] = useState(params.get("email") || "");
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  // reschedule UI
  const [rescheduling, setRescheduling] = useState(false);
  const [slots, setSlots] = useState([]);
  const [newDate, setNewDate] = useState(null);
  const [newSlot, setNewSlot] = useState("");
  const [availability, setAvailability] = useState(null);
  const dates = useMemo(() => generateNext30Days(), []);

  useEffect(() => {
    api.get("/time-slots").then((r) => setSlots(r.data.slots || [])).catch(() => {});
  }, []);

  // Auto-lookup if conf+email are in URL
  useEffect(() => {
    if (conf && email && !booking && !err) {
      lookup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!newDate) { setAvailability(null); return; }
    api.get(`/availability?date=${toISODate(newDate)}`)
      .then((r) => setAvailability(r.data))
      .catch(() => setAvailability(null));
  }, [newDate]);

  const lookup = useCallback(async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setErr(""); setMsg(""); setLoading(true);
    try {
      const { data } = await api.post("/customer/lookup", {
        confirmation: conf.trim().toUpperCase(),
        email: email.trim().toLowerCase(),
      });
      setBooking(data);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
      setBooking(null);
    } finally {
      setLoading(false);
    }
  }, [conf, email]);

  const doReschedule = async () => {
    if (!newDate || !newSlot) return;
    setErr(""); setMsg(""); setLoading(true);
    try {
      const { data } = await api.post("/customer/reschedule", {
        confirmation: conf,
        email: email.trim().toLowerCase(),
        preferred_date: toISODate(newDate),
        time_slot: newSlot,
      });
      setBooking(data.booking);
      setMsg(data.message || "Rescheduled.");
      setRescheduling(false);
      setNewDate(null); setNewSlot("");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  const doCancel = async () => {
    if (!window.confirm("Cancel this booking? This frees up your slot and you'll lose any held deposit hold.")) return;
    setErr(""); setMsg(""); setLoading(true);
    try {
      const { data } = await api.post("/customer/cancel", {
        confirmation: conf,
        email: email.trim().toLowerCase(),
      });
      setBooking(data.booking);
      setMsg(data.message || "Cancelled.");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-bone min-h-[80vh]" data-testid="customer-portal-page">
      <section className="section-pad">
        <div className="max-w-3xl mx-auto">
          <div className="eyebrow mb-3">Customer portal</div>
          <h1 className="font-heading text-5xl md:text-6xl tracking-tighter font-light">
            Manage your <span className="text-forest">booking.</span>
          </h1>
          <p className="mt-5 text-muted2">
            Enter your confirmation number and email to view, reschedule, or cancel — no login required.
          </p>

          {/* Lookup form */}
          {!booking && (
            <form onSubmit={lookup} className="mt-10 card-surface p-8" data-testid="lookup-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="eyebrow block mb-2">Confirmation #</label>
                  <input
                    className="input-base uppercase tracking-widest"
                    placeholder="A1B2C3D4"
                    value={conf}
                    onChange={(e) => setConf(e.target.value.toUpperCase())}
                    required
                    data-testid="portal-conf"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-2">Email</label>
                  <input
                    className="input-base"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="portal-email"
                  />
                </div>
              </div>
              {err && (
                <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-xl" data-testid="portal-error">
                  {err}
                </div>
              )}
              <button type="submit" disabled={loading} className="btn-primary mt-6 w-full md:w-auto" data-testid="portal-lookup">
                <Search size={14} /> {loading ? "Looking up…" : "Find my booking"}
              </button>
              <p className="text-xs text-muted2 mt-4">
                Tip: your confirmation number is in your booking email subject line and at the top of the confirmation message.
              </p>
            </form>
          )}

          {/* Booking detail + actions */}
          {booking && (
            <div className="mt-10 space-y-6" data-testid="portal-booking">
              {msg && (
                <div className="text-sm text-forest bg-forest/8 border border-forest/20 px-4 py-3 rounded-xl flex items-center gap-2" data-testid="portal-message">
                  <CheckCircle2 size={14} /> {msg}
                </div>
              )}
              {err && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-xl flex items-center gap-2">
                  <AlertTriangle size={14} /> {err}
                </div>
              )}

              <div className="card-surface p-8">
                <div className="flex items-start justify-between gap-4 mb-6">
                  <div>
                    <div className="eyebrow">Confirmation #</div>
                    <div className="font-heading text-2xl tracking-widest mt-1" data-testid="portal-booking-conf">
                      {booking.id.split("-")[0].toUpperCase()}
                    </div>
                  </div>
                  <span
                    className={`text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full ${STATUS_STYLES[booking.status] || "bg-gray-100 text-gray-700"}`}
                    data-testid="portal-status"
                  >
                    {booking.status}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className="eyebrow mb-1">Service</div>
                    <div className="font-heading text-lg">{booking.package_name}</div>
                    {booking.add_ons?.length > 0 && (
                      <div className="text-xs text-muted2 mt-1">+ {booking.add_ons.length} add-on(s)</div>
                    )}

                    <div className="eyebrow mt-5 mb-1">When</div>
                    <div className="flex items-center gap-2 text-base">
                      <Calendar size={14} className="text-forest" /> {booking.preferred_date}
                    </div>
                    <div className="flex items-center gap-2 text-base mt-1">
                      <Clock size={14} className="text-forest" /> {booking.time_slot}
                    </div>
                  </div>

                  <div>
                    <div className="eyebrow mb-1">Service address</div>
                    <div className="flex items-start gap-2">
                      <MapPin size={14} className="text-forest mt-1 shrink-0" />
                      <div>
                        <div>{booking.address}</div>
                        <div className="text-muted2 text-sm">{booking.city}, {booking.state} {booking.zip_code || ""}</div>
                      </div>
                    </div>

                    <div className="eyebrow mt-5 mb-1">Contact</div>
                    <div className="flex items-center gap-2 text-sm"><Mail size={12} /> {booking.email}</div>
                    <div className="flex items-center gap-2 text-sm mt-1"><Phone size={12} /> {booking.phone}</div>
                  </div>
                </div>

                <div className="border-t border-black/10 mt-7 pt-5 flex items-center justify-between">
                  <span className="eyebrow">Estimated total</span>
                  <div className="text-right">
                    <span className="font-heading text-2xl">${booking.estimated_total}</span>
                    {booking.deposit_paid && (
                      <div className="text-[10px] uppercase tracking-[0.18em] text-forest mt-1 flex items-center gap-1 justify-end">
                        <CheckCircle2 size={10} /> ${booking.deposit_amount || 49} deposit paid
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              {booking.status !== "cancelled" && booking.status !== "completed" && (
                <div className="card-surface p-8">
                  {!rescheduling ? (
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={() => setRescheduling(true)}
                        className="btn-primary flex-1 justify-center"
                        data-testid="portal-start-reschedule"
                      >
                        <RefreshCw size={14} /> Reschedule
                      </button>
                      <button
                        onClick={doCancel}
                        disabled={loading}
                        className="btn-ghost flex-1 justify-center text-red-700 border-red-200"
                        data-testid="portal-cancel"
                      >
                        <XCircle size={14} /> Cancel booking
                      </button>
                    </div>
                  ) : (
                    <div data-testid="portal-reschedule">
                      <button
                        onClick={() => { setRescheduling(false); setNewDate(null); setNewSlot(""); }}
                        className="text-sm text-muted2 hover:text-ink mb-5 inline-flex items-center gap-1"
                      >
                        <ArrowLeft size={12} /> Back
                      </button>
                      <h3 className="font-heading text-2xl tracking-tight mb-4">Pick a new date</h3>
                      <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-10 gap-2">
                        {dates.map((d) => {
                          const iso = toISODate(d);
                          const active = newDate && toISODate(newDate) === iso;
                          return (
                            <button
                              key={iso}
                              onClick={() => { setNewDate(d); setNewSlot(""); }}
                              className={`p-3 rounded-xl border text-center transition ${
                                active ? "bg-forest text-white border-forest" : "bg-white border-black/10 hover:border-forest/40"
                              }`}
                              data-testid={`portal-date-${iso}`}
                            >
                              <div className="text-[10px] uppercase tracking-[0.18em] opacity-70">
                                {d.toLocaleDateString("en-US", { weekday: "short" })}
                              </div>
                              <div className="font-heading text-lg">{d.getDate()}</div>
                            </button>
                          );
                        })}
                      </div>

                      {newDate && (
                        <>
                          <h3 className="font-heading text-2xl tracking-tight mt-8 mb-4">Pick a time</h3>
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                            {slots.map((s) => {
                              const info = availability?.slots?.find((x) => x.slot === s);
                              const full = info?.full;
                              const isActive = newSlot === s;
                              return (
                                <button
                                  key={s}
                                  onClick={() => !full && setNewSlot(s)}
                                  disabled={full}
                                  className={`p-4 rounded-xl border text-center font-heading transition ${
                                    full
                                      ? "bg-bone border-black/5 text-muted2/60 cursor-not-allowed"
                                      : isActive
                                      ? "bg-forest text-white border-forest"
                                      : "bg-white border-black/10 hover:border-forest/40"
                                  }`}
                                  data-testid={`portal-slot-${s.replace(/[\s:]/g, "")}`}
                                >
                                  <div>{s}</div>
                                  {info && (
                                    <div className={`text-[10px] uppercase tracking-[0.18em] mt-1 ${full ? "text-red-700" : isActive ? "text-white/80" : "text-muted2"}`}>
                                      {full ? "Full" : `${info.remaining} left`}
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </>
                      )}

                      <button
                        onClick={doReschedule}
                        disabled={!newDate || !newSlot || loading}
                        className="btn-primary mt-8 disabled:opacity-50"
                        data-testid="portal-confirm-reschedule"
                      >
                        {loading ? "Saving…" : "Confirm new time"}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {(booking.status === "cancelled" || booking.status === "completed") && (
                <div className="card-surface p-6 text-center">
                  <p className="text-muted2 text-sm">
                    This booking is <strong>{booking.status}</strong>. To request a new appointment,
                  </p>
                  <Link to="/book" className="btn-primary mt-4">Book again</Link>
                </div>
              )}

              <div>
                <button
                  onClick={() => { setBooking(null); setMsg(""); setErr(""); }}
                  className="text-sm text-muted2 hover:text-forest"
                  data-testid="portal-back-lookup"
                >
                  ← Look up a different booking
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
